/**
 * Structure template library: built-in starting points + conversion between templates and project instances.
 * Built-in defaults are illustrative (see kinds.ts) and are not compliant designs by themselves.
 */
import type { StructureGeometry } from './generate';
import { DEFAULT_PARAMS, DEFAULT_SECTIONS, paramDefs, type MemberRole, type StructureKind } from './kinds';
import {
  STRUCTURE_ENGINE_VERSION,
  TEMPLATE_CATEGORY_LABEL,
  TEMPLATE_SCHEMA,
  TEMPLATE_VERSION,
  migrateTemplate,
  type StructureInstance,
  type StructureTemplate,
} from './schema';
import { BUILTIN_SECTIONS, type SteelSection } from './sections';
import { connectionSummary } from './validate';

type Cat = StructureTemplate['categories'];
const BUILT_AT = '2026-10-26T00:00:00.000Z';

function builtIn(
  id: string,
  name: string,
  kind: StructureKind,
  categories: Cat,
  description: string,
  params: Record<string, number | string | boolean> = {},
  attach: 'RAILS' | 'MINI_RAILS' | 'PURLINS' = 'PURLINS',
): StructureTemplate {
  return {
    schema: TEMPLATE_SCHEMA,
    version: TEMPLATE_VERSION,
    id,
    name,
    description,
    kind,
    revision: 1,
    engineVersion: STRUCTURE_ENGINE_VERSION,
    categories,
    params: { ...DEFAULT_PARAMS[kind], ...params },
    sections: { ...(DEFAULT_SECTIONS[kind] as Record<string, string>) },
    overrides: {},
    added: [],
    sectionDefs: [],
    pvInterface: { attach, supportLinesPerRow: 2, clampZonePct: null },
    memberSummary: [],
    connections: [],
    builtIn: true,
    createdAt: BUILT_AT,
    updatedAt: BUILT_AT,
  };
}

/** Deep copy of plain JSON data. */
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

const ILL =
  'Thông số mặc định chỉ mang tính minh hoạ — kỹ sư phải kiểm tra và tính toán kết cấu cho công trình thực tế.';

export const BUILTIN_TEMPLATES: StructureTemplate[] = [
  builtIn(
    'tpl-roof-rail',
    'Ray nhôm song song mái (nhà dân)',
    'ROOF_RAIL',
    {
      segment: 'RESIDENTIAL',
      roofTypes: ['PITCHED', 'METAL'],
      mounting: 'ROOF_PARALLEL',
      clearance: 'LOW',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Hai rail nhôm mỗi hàng tấm, chân L / móc mái theo bước tối đa 1.200 mm, kẹp biên & kẹp giữa. ${ILL}`,
    {},
    'RAILS',
  ),
  builtIn(
    'tpl-minirail',
    'MiniRail thấp cho mái tôn',
    'MINI_RAIL',
    {
      segment: 'BOTH',
      roofTypes: ['METAL'],
      mounting: 'ROOF_PARALLEL',
      clearance: 'LOW',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Đoạn MiniRail bắt trực tiếp lên sóng tôn tại mỗi mép tấm, 2 hàng kẹp mỗi hàng tấm. ${ILL}`,
    {},
    'MINI_RAILS',
  ),
  builtIn(
    'tpl-tilt-flat',
    'Khung nghiêng 15° cho mái bằng',
    'TILT_FRAME',
    {
      segment: 'BOTH',
      roofTypes: ['FLAT'],
      mounting: 'TILTED',
      clearance: 'LOW',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Khung tam giác mỗi hàng tấm: chân trước / sau, kèo nghiêng, thanh đế, giằng chéo; góc nghiêng chỉnh được. Cần khoảng cách hàng đủ để không che bóng. ${ILL}`,
  ),
  builtIn(
    'tpl-elevated-row',
    'Khung thép nâng một hàng (2 cột)',
    'ELEVATED_ROW',
    {
      segment: 'RESIDENTIAL',
      roofTypes: ['FLAT', 'PITCHED'],
      mounting: 'ELEVATED',
      clearance: 'MEDIUM',
      rows: 'SINGLE',
      profile: 'STANDARD',
    },
    `Mỗi hàng tấm một dãy khung: cột trước / sau, kèo, xà gồ C, giằng góc và giằng dọc hai nhịp đầu. ${ILL}`,
  ),
  builtIn(
    'tpl-elevated-row-t',
    'Khung thép nâng một hàng (cột giữa chữ T)',
    'ELEVATED_ROW',
    {
      segment: 'BOTH',
      roofTypes: ['FLAT', 'METAL'],
      mounting: 'ELEVATED',
      clearance: 'HIGH',
      rows: 'SINGLE',
      profile: 'STANDARD',
    },
    `Một cột giữa mỗi khung, kèo công-xôn hai phía, giằng góc hai bên. ${ILL}`,
    { columnLayout: 'ONE', heightMm: 3000 },
  ),
  builtIn(
    'tpl-elevated-multi',
    'Khung thép nâng nhiều hàng (mái bằng / mái tôn)',
    'ELEVATED_MULTI',
    {
      segment: 'CI',
      roofTypes: ['FLAT', 'METAL'],
      mounting: 'ELEVATED',
      clearance: 'MEDIUM',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Lưới cột – dầm chính dọc hàng, kèo trên dầm, xà gồ C theo hàng tấm, giằng chéo hai nhịp đầu. ${ILL}`,
  ),
  builtIn(
    'tpl-canopy-parallel',
    'Khung nâng song song mái tôn (che mái)',
    'ELEVATED_MULTI',
    {
      segment: 'CI',
      roofTypes: ['METAL', 'PITCHED'],
      mounting: 'ELEVATED',
      clearance: 'MEDIUM',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Mặt tấm pin song song mái hiện hữu, nâng cao bằng cột thép đứng — dùng khi cần lối bảo trì dưới tấm. ${ILL}`,
    { tiltMode: 'ROOF', heightMm: 1800 },
  ),
  builtIn(
    'tpl-portal-ci',
    'Khung cổng lặp lại cho nhà xưởng (C&I)',
    'PORTAL',
    {
      segment: 'CI',
      roofTypes: ['METAL', 'FLAT'],
      mounting: 'ELEVATED',
      clearance: 'HIGH',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Khung cổng cột H – kèo I lặp lại theo bước khung, xà gồ C, dầm biên, giằng góc và giằng chéo mặt mái hai nhịp đầu. ${ILL}`,
  ),
  builtIn(
    'tpl-column-beam',
    'Kết cấu cột – dầm hai phương (giàn phẳng)',
    'COLUMN_BEAM',
    {
      segment: 'CI',
      roofTypes: ['FLAT'],
      mounting: 'ELEVATED',
      clearance: 'HIGH',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Lưới cột, dầm hai phương cùng cao độ, xà gồ đều, giằng chéo hai nhịp đầu. ${ILL}`,
  ),
  builtIn(
    'tpl-custom',
    'Kết cấu tự dựng (bắt đầu từ 4 cột)',
    'CUSTOM',
    {
      segment: 'BOTH',
      roofTypes: ['FLAT', 'PITCHED', 'METAL'],
      mounting: 'ELEVATED',
      clearance: 'MEDIUM',
      rows: 'MULTI',
      profile: 'CUSTOM',
    },
    `Mặt khung theo kích thước nhập, 4 cột góc và dầm biên; thêm / sửa từng cấu kiện trong cảnh 3D. ${ILL}`,
    { sizeMode: 'MANUAL' },
  ),
  builtIn(
    'tpl-carport-mono',
    'Nhà để xe một dốc — 2 chỗ, khung độc lập',
    'CARPORT',
    {
      segment: 'RESIDENTIAL',
      roofTypes: ['GROUND'],
      mounting: 'CARPORT',
      clearance: 'HIGH',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Khung thép độc lập trên mặt đất: 2 hàng cột, dầm dọc đầu cột, kèo dốc 5°, xà gồ C; tấm pin đặt trên xà gồ. ${ILL}`,
  ),
  builtIn(
    'tpl-carport-gable',
    'Nhà để xe hai dốc — cột giữa (chữ T)',
    'CARPORT',
    {
      segment: 'RESIDENTIAL',
      roofTypes: ['GROUND'],
      mounting: 'CARPORT',
      clearance: 'HIGH',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Một hàng cột giữa, kèo hai dốc công-xôn hai phía, xà gồ C; tấm pin trên xà gồ hai mặt dốc. ${ILL}`,
    { roofType: 'GABLE', columnLayout: 'CENTER', widthMm: 6000, frontOverhangMm: 0, rearOverhangMm: 0, heightMm: 2600, slopeDeg: 8 },
  ),
  builtIn(
    'tpl-carport-rail',
    'Nhà để xe một dốc — rail nhôm trên xà gồ',
    'CARPORT',
    {
      segment: 'RESIDENTIAL',
      roofTypes: ['GROUND'],
      mounting: 'CARPORT',
      clearance: 'HIGH',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Như mẫu một dốc, thêm lớp rail nhôm vuông góc xà gồ; tấm pin kẹp trên rail. ${ILL}`,
    { railLayer: 'RAIL', purlinSpacingMm: 1500 },
    'RAILS',
  ),  builtIn(
    'tpl-carport-butterfly',
    'Nhà để xe mái chữ V — cột giữa, 2 × 3 m',
    'CARPORT',
    {
      segment: 'RESIDENTIAL',
      roofTypes: ['GROUND'],
      mounting: 'CARPORT',
      clearance: 'HIGH',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Mẫu 1: một hàng cột giữa, kèo hai phía dốc ngược 8° (mái bướm, thoát nước giữa), giằng góc hai phía; cao mép mái ~2,8 m. ${ILL}`,
    { roofType: 'GABLE', columnLayout: 'CENTER', slopeDeg: -8, widthMm: 6000, frontOverhangMm: 0, rearOverhangMm: 0, heightMm: 2500, kneeLengthMm: 700 },
  ),
  builtIn(
    'tpl-carport-butterfly-wide',
    'Nhà để xe mái chữ V lớn — cột giữa, rộng 8 m',
    'CARPORT',
    {
      segment: 'RESIDENTIAL',
      roofTypes: ['GROUND'],
      mounting: 'CARPORT',
      clearance: 'HIGH',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Mẫu 2: một hàng cột giữa, kèo công-xôn 4 m mỗi phía dốc ngược 15° (cao mép ~3,3 m, giữa ~2,1 m), giằng góc dài; kèo cong của bản vẽ quy về kèo thẳng. ${ILL}`,
    { roofType: 'GABLE', columnLayout: 'CENTER', slopeDeg: -15, widthMm: 8000, frontOverhangMm: 0, rearOverhangMm: 0, heightMm: 3000, parkingBays: 3, lengthMm: 8100, kneeLengthMm: 1200 },
  ),
  builtIn(
    'tpl-carport-wall',
    'Nhà để xe công-xôn tựa tường — vươn 3 m',
    'CARPORT',
    {
      segment: 'RESIDENTIAL',
      roofTypes: ['GROUND'],
      mounting: 'CARPORT',
      clearance: 'HIGH',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Mẫu 3: một hàng cột sát tường / cột BTCT phía sau (bu lông neo), kèo công-xôn 3 m dốc 3° ra phía trước, giằng góc phía trước. ${ILL}`,
    { columnLayout: 'BACK', slopeDeg: 3, widthMm: 3000, frontOverhangMm: 0, rearOverhangMm: 100, heightMm: 2650, kneeLengthMm: 600 },
  ),
  builtIn(
    'tpl-carport-stay',
    'Nhà để xe hai cánh công-xôn 5,5 m — cột vươn + thanh căng',
    'CARPORT',
    {
      segment: 'RESIDENTIAL',
      roofTypes: ['GROUND'],
      mounting: 'CARPORT',
      clearance: 'HIGH',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Mẫu 4: hai hàng cột cách 1 m, mỗi phía công-xôn 5,5 m dốc ngược 6° về máng giữa; đỉnh cột vươn 2 m trên mái, thanh căng xiên xuống kèo. ${ILL}`,
    { roofType: 'GABLE', columnLayout: 'TWO', slopeDeg: -6, widthMm: 1000, frontOverhangMm: 5500, rearOverhangMm: 5500, heightMm: 2400, kneeBrace: false, stayRod: true, stayMastMm: 2000, stayAtPct: 70, parkingBays: 2, lengthMm: 5400 },
  ),
  builtIn(
    'tpl-carport-offset',
    'Nhà để xe một hàng cột lệch tâm — 1,8 m + 4,2 m',
    'CARPORT',
    {
      segment: 'RESIDENTIAL',
      roofTypes: ['GROUND'],
      mounting: 'CARPORT',
      clearance: 'HIGH',
      rows: 'MULTI',
      profile: 'STANDARD',
    },
    `Mẫu 5: một hàng cột, sau 1,8 m / trước 4,2 m, mái một dốc cao dần ra phía trước 5° (góc cột – kèo > 90°), giằng góc dài 1 m; cao đỉnh ~3 m. ${ILL}`,
    { columnLayout: 'CENTER', slopeDeg: -5, widthMm: 3600, frontOverhangMm: 2400, rearOverhangMm: 0, heightMm: 2550, kneeLengthMm: 1000 },
  ),
];

/** New project instance from a template. */
export function instanceFromTemplate(
  t: StructureTemplate,
  o: {
    id: string;
    roofUid: string | null;
    roofIndex: number | null;
    name?: string;
    params?: Record<string, number | string | boolean>;
  },
): StructureInstance {
  return {
    id: o.id,
    name: o.name ?? t.name,
    kind: t.kind,
    templateId: t.id,
    templateRevision: t.revision,
    roofUid: o.roofUid,
    roofIndex: o.roofIndex,
    params: { ...t.params, ...(o.params ?? {}) },
    sections: { ...t.sections },
    overrides: clone(t.overrides),
    added: clone(t.added),
    ...(t.mounting ? { mounting: clone(t.mounting) } : {}),
  };
}

/** Save a (customised) project structure as a reusable template. */
export function templateFromInstance(
  inst: StructureInstance,
  geometry: StructureGeometry | null,
  sections: Map<string, SteelSection>,
  meta: {
    id: string;
    name: string;
    description: string;
    categories: Cat;
    createdBy?: string;
    now?: Date;
    previous?: StructureTemplate;
  },
): StructureTemplate {
  const builtinIds = new Set(BUILTIN_SECTIONS.map((s) => s.id));
  const usedIds = new Set<string>([
    ...Object.values(inst.sections),
    ...Object.values(inst.overrides).flatMap((o) => (o.sectionId ? [o.sectionId] : [])),
    ...inst.added.map((a) => a.sectionId),
    ...(geometry?.members.map((m) => m.sectionId) ?? []),
  ]);
  const summary = new Map<string, { role: MemberRole; sectionId: string; count: number }>();
  for (const m of geometry?.members ?? []) {
    const k = `${m.role}|${m.sectionId}`;
    const e = summary.get(k) ?? { role: m.role, sectionId: m.sectionId, count: 0 };
    e.count++;
    summary.set(k, e);
  }
  const now = (meta.now ?? new Date()).toISOString();
  const zone = [Number(inst.params.clampZoneMinPct ?? 0), Number(inst.params.clampZoneMaxPct ?? 0)] as [
    number,
    number,
  ];
  return {
    schema: TEMPLATE_SCHEMA,
    version: TEMPLATE_VERSION,
    id: meta.id,
    name: meta.name,
    description: meta.description,
    kind: inst.kind,
    revision: (meta.previous?.revision ?? 0) + 1,
    engineVersion: STRUCTURE_ENGINE_VERSION,
    categories: meta.categories,
    params: { ...inst.params },
    sections: { ...inst.sections },
    overrides: clone(inst.overrides),
    added: clone(inst.added),
    ...(inst.mounting ? { mounting: clone(inst.mounting) } : {}),
    sectionDefs: [...usedIds]
      .filter((id) => !builtinIds.has(id) && sections.has(id))
      .map((id) => sections.get(id)!),
    pvInterface: {
      attach:
        inst.kind === 'ROOF_RAIL' || inst.params.railLayer === 'RAIL'
          ? 'RAILS'
          : inst.kind === 'MINI_RAIL' || inst.params.railLayer === 'MINI_RAIL'
            ? 'MINI_RAILS'
            : 'PURLINS',
      supportLinesPerRow: Number(inst.params.railsPerRow ?? 2),
      clampZonePct: zone[1] > zone[0] ? zone : null,
    },
    memberSummary: [...summary.values()].slice(0, 50),
    connections: geometry ? connectionSummary(geometry, sections).slice(0, 100) : [],
    builtIn: false,
    createdAt: meta.previous?.createdAt ?? now,
    updatedAt: now,
    ...(meta.createdBy ? { createdBy: meta.createdBy } : {}),
  };
}

export interface TemplateFilter {
  query?: string;
  segment?: string;
  roofType?: string;
  mounting?: string;
  clearance?: string;
  rows?: string;
  profile?: string;
}

const fold = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

/** Search (accent-insensitive) and filter by category. */
export function filterTemplates(list: readonly StructureTemplate[], f: TemplateFilter): StructureTemplate[] {
  const q = f.query ? fold(f.query.trim()) : '';
  return list.filter((t) => {
    const c = t.categories;
    if (f.segment && c.segment !== f.segment && c.segment !== 'BOTH') return false;
    if (f.roofType && !c.roofTypes.includes(f.roofType as Cat['roofTypes'][number])) return false;
    if (f.mounting && c.mounting !== f.mounting) return false;
    if (f.clearance && c.clearance !== f.clearance) return false;
    if (f.rows && c.rows !== f.rows) return false;
    if (f.profile && c.profile !== f.profile) return false;
    if (!q) return true;
    const L = TEMPLATE_CATEGORY_LABEL;
    const hay = fold(
      [
        t.name,
        t.description,
        L.segment[c.segment],
        L.mounting[c.mounting],
        L.clearance[c.clearance],
        L.rows[c.rows],
        L.profile[c.profile],
        ...c.roofTypes.map((r) => L.roofTypes[r]),
      ].join(' '),
    );
    return q.split(/\s+/).every((w) => hay.includes(w));
  });
}

/** Template export file (a single template or a library). */
export function templatesToFile(list: StructureTemplate[]): string {
  return JSON.stringify(
    list.length === 1
      ? list[0]
      : { schema: `${TEMPLATE_SCHEMA}-library`, version: TEMPLATE_VERSION, templates: list },
    null,
    1,
  );
}

/** Read an exported file (single template or library). Invalid entries are reported, valid ones returned. */
export function templatesFromFile(text: string): { templates: StructureTemplate[]; errors: string[] } {
  let o: unknown;
  try {
    o = JSON.parse(text);
  } catch {
    return { templates: [], errors: ['Tệp không phải JSON hợp lệ.'] };
  }
  const raw =
    o && typeof o === 'object' && Array.isArray((o as { templates?: unknown }).templates)
      ? ((o as { templates: unknown[] }).templates as unknown[])
      : [o];
  const templates: StructureTemplate[] = [];
  const errors: string[] = [];
  raw.forEach((r, i) => {
    const m = migrateTemplate(r);
    if (m.ok) templates.push({ ...m.value, builtIn: false });
    else errors.push(raw.length > 1 ? `Mẫu ${i + 1}: ${m.error}` : m.error);
  });
  return { templates, errors };
}

/** Parameter definitions documented in an exported template (for readers of the file). */
export const templateParamDocs = (kind: StructureKind) =>
  paramDefs(kind).map((d) => ({
    key: d.key,
    label: d.label,
    unit: d.unit ?? null,
    min: d.min ?? null,
    max: d.max ?? null,
  }));
